import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { get } from '../../../../../redux/api';
import * as reportGeneration from '../../../../../redux/reportGeneration';
import { Icon, icons, LoaderButton } from '../../../../generic';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../../generic/Button';
import TooltipButton from '../../../../generic/Button/TooltipButton';
import CopyToClipboard from '../../../../generic/CopyToClipboard';
import { getGenerateReportURL } from '../projectLevel/utils';
import type { Report } from '../../../../../../shared/model-typings';
import type { ProjectContext, ReportCategory, ReportExclusionOptions } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
};

const PublicLinkButton: VoidFunctionComponent<Props> = ({
  format,
  reportType,
  includeOptions,
  dependencyMetadataOptions,
  excludeOptions,
  isDisabled,
  authorName,
  authorEmail,
  supplier,
}) => {
  const reportContext = useSelector(reportGeneration.selectors.getReportContext) as ProjectContext;
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const addToast = useAddToast();

  /**
   * Reset the public URL when the format, report context, include options, or dependency metadata options change.
   * This is to ensure that the user can generate a new public link if they change any of these settings.
   */
  useEffect(() => {
    setPublicUrl(null);
  }, [format, reportContext, includeOptions, dependencyMetadataOptions, reportType]);

  const onGenerate = async () => {
    const url = getGenerateReportURL({
      projectRevisionLocator: reportContext.revision,
      format,
      reportType,
      includeOptions,
      dependencyMetadataOptions,
      authorName,
      authorEmail,
      supplier,
      excludeOptions,
    });

    try {
      setIsLoading(true);
      const response = await get<Report>(url);
      if (response.data?.publicURL) {
        setPublicUrl(response.data?.publicURL);
      }
    } catch (error) {
      addToast({
        content: 'Failed to generate public link',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return publicUrl ? (
    <CopyToClipboard text={publicUrl} showCopyIcon={false}>
      <TooltipButton
        id="public-link"
        tooltip={publicUrl}
        type={BUTTON_TYPE.SECONDARY}
        onClick={() => {
          setIsCopied(true);
          addToast({
            content: 'Public link copied to clipboard',
            type: 'success',
          });
        }}
        size={BUTTON_SIZE.LARGE}
        isDisabled={isDisabled}
      >
        <Icon icon={isCopied ? icons.check : icons.copy} /> Copy Public Link
      </TooltipButton>
    </CopyToClipboard>
  ) : (
    <LoaderButton
      isLoading={isLoading}
      type={BUTTON_TYPE.SECONDARY}
      onClick={onGenerate}
      size={BUTTON_SIZE.LARGE}
      isDisabled={isDisabled}
    >
      <Icon icon={icons.globeOutline} /> Generate Download URL
    </LoaderButton>
  );
};

export default PublicLinkButton;
